import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Calculator, Trash2, Search, X, Share2, Download, ChevronDown, Plus, LogIn, LogOut
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Log para debugging
console.log('PWA API_URL:', API_URL || 'NO CONFIGURADA (usando misma URL)');

export default function QuotationPWA() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [token, setToken] = useState(localStorage.getItem('pwa_token') || '');
  
  const [priceLists, setPriceLists] = useState([]);
  const [studies, setStudies] = useState([]);
  const [studyPrices, setStudyPrices] = useState([]);
  const [quoteConfig, setQuoteConfig] = useState(null);
  
  const [activePriceListId, setActivePriceListId] = useState("");
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [formData, setFormData] = useState({
    patient_name: "",
    discount: 0,
    instructions: "",
    valid_days: 0,
  });
  
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [studySearchTerm, setStudySearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  
  const searchInputRef = useRef(null);

  // Check if token exists on mount
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setIsAuthenticated(true);
        fetchData();
      } else {
        localStorage.removeItem('pwa_token');
        setToken('');
      }
    } catch (error) {
      console.error('Token validation error:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log('PWA: Intentando login...');
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token;
        console.log('PWA: Login exitoso, token:', newToken?.substring(0, 20) + '...');
        
        localStorage.setItem('pwa_token', newToken);
        setToken(newToken);
        setIsAuthenticated(true);
        
        // Llamar fetchData con el nuevo token directamente
        await fetchDataWithToken(newToken);
        toast.success('Sesión iniciada');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('PWA: Error login:', response.status, errorData);
        toast.error('Credenciales incorrectas');
      }
    } catch (error) {
      console.error('PWA: Error de conexión:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('pwa_token');
    setToken('');
    setIsAuthenticated(false);
    setSelectedStudies([]);
    setPriceLists([]);
    setStudies([]);
  };

  const apiCallWithToken = async (endpoint, authToken, options = {}) => {
    try {
      console.log(`PWA API Call: ${endpoint}`);
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          ...options.headers
        }
      });
      
      if (!response.ok) {
        console.error(`PWA API Error ${endpoint}: ${response.status}`);
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      console.log(`PWA API Response ${endpoint}:`, Array.isArray(data) ? `${data.length} items` : 'object');
      return data;
    } catch (error) {
      console.error(`PWA API Exception ${endpoint}:`, error);
      throw error;
    }
  };

  const apiCall = async (endpoint, options = {}) => {
    return apiCallWithToken(endpoint, token, options);
  };

  const fetchDataWithToken = async (authToken) => {
    try {
      setLoading(true);
      console.log('PWA: Iniciando carga de datos con token...');
      
      // Cargar datos por separado para mejor manejo de errores
      let studiesData = [];
      let priceListsData = [];
      let quoteConfigData = {};
      
      try {
        studiesData = await apiCallWithToken('/api/studies', authToken);
        console.log('PWA: Estudios cargados:', studiesData?.length);
      } catch (e) {
        console.error('PWA: Error cargando estudios:', e);
        toast.error('Error cargando estudios');
      }
      
      try {
        priceListsData = await apiCallWithToken('/api/price-lists', authToken);
        console.log('PWA: Listas de precios cargadas:', priceListsData?.length, priceListsData);
      } catch (e) {
        console.error('PWA: Error cargando listas de precios:', e);
        toast.error('Error cargando listas de precios');
      }
      
      try {
        quoteConfigData = await apiCallWithToken('/api/quote-format', authToken);
      } catch (e) {
        console.error('PWA: Error cargando config:', e);
      }
      
      // Procesar estudios
      if (studiesData && Array.isArray(studiesData)) {
        setStudies(studiesData.filter(s => s.active));
      }
      
      setQuoteConfig(quoteConfigData || {});
      
      // Procesar listas de precios
      if (priceListsData && Array.isArray(priceListsData) && priceListsData.length > 0) {
        // Ordenar listas: PARTICULAR primero, luego DIF, luego el resto
        const sortedPriceLists = [...priceListsData].sort((a, b) => {
          const nameA = a.name?.toUpperCase() || '';
          const nameB = b.name?.toUpperCase() || '';
          if (nameA.includes('PARTICULAR')) return -1;
          if (nameB.includes('PARTICULAR')) return 1;
          if (nameA.includes('DIF')) return -1;
          if (nameB.includes('DIF')) return 1;
          return nameA.localeCompare(nameB);
        });
        setPriceLists(sortedPriceLists);
        console.log('PWA: Listas ordenadas:', sortedPriceLists.map(p => p.name));
        
        // Seleccionar la primera lista (PARTICULAR) por defecto
        const firstList = sortedPriceLists[0];
        setActivePriceListId(firstList.id);
        
        try {
          const pricesRes = await apiCallWithToken(`/api/study-prices?price_list_id=${firstList.id}`, authToken);
          if (pricesRes && Array.isArray(pricesRes)) {
            setStudyPrices(pricesRes);
            console.log('PWA: Precios cargados:', pricesRes.length);
          }
        } catch (e) {
          console.error('PWA: Error cargando precios:', e);
        }
      } else {
        console.warn('PWA: No se encontraron listas de precios');
        toast.error('No se encontraron listas de precios');
      }
      
    } catch (error) {
      console.error("PWA: Error general:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    await fetchDataWithToken(token);
  };

  const fetchStudyPrices = async (priceListId) => {
    if (!priceListId) return;
    try {
      const response = await apiCall(`/api/study-prices?price_list_id=${priceListId}`);
      setStudyPrices(response);
      return response;
    } catch (error) {
      console.error("Error fetching study prices:", error);
      return [];
    }
  };

  const handlePriceListChange = async (priceListId) => {
    if (!priceListId) return;
    setActivePriceListId(priceListId);
    const newPrices = await fetchStudyPrices(priceListId);
    // Recalcular precios de estudios ya seleccionados con los nuevos precios
    if (newPrices && newPrices.length > 0) {
      setSelectedStudies(prev => prev.map(s => {
        const priceEntry = newPrices.find(p => p.study_id === s.study_id);
        const study = studies.find(st => st.id === s.study_id);
        const newPrice = priceEntry?.price || study?.base_price || study?.price || 0;
        return { ...s, price: newPrice };
      }));
    }
  };

  const getStudyPrice = (studyId) => {
    return getStudyPriceFromList(studyId, activePriceListId);
  };

  const getStudyPriceFromList = (studyId, priceListId) => {
    const priceEntry = studyPrices.find(p => p.study_id === studyId);
    if (priceEntry) return priceEntry.price;
    const study = studies.find(s => s.id === studyId);
    return study?.base_price || study?.price || 0;
  };

  const filteredStudies = studies.filter(study => {
    if (!studySearchTerm) return true;
    const searchLower = studySearchTerm.toLowerCase();
    const nameMatch = study.name?.toLowerCase().includes(searchLower);
    const codeMatch = study.code?.toLowerCase().includes(searchLower);
    const alreadyAdded = selectedStudies.some(s => s.study_id === study.id);
    return (nameMatch || codeMatch) && !alreadyAdded;
  });

  const addStudy = (study) => {
    const price = getStudyPrice(study.id);
    const newStudy = {
      study_id: study.id,
      study_name: study.name,
      study_code: study.code || "",
      price: price
    };
    setSelectedStudies(prev => [...prev, newStudy]);
    setStudySearchTerm("");
    toast.success(`${study.name} agregado`);
  };

  const removeStudy = (studyId) => {
    setSelectedStudies(prev => prev.filter(s => s.study_id !== studyId));
  };

  const subtotal = selectedStudies.reduce((sum, s) => sum + (s.price || 0), 0);
  const discountAmount = formData.discount || 0;
  const total = Math.max(0, subtotal - discountAmount);

  const generatePDF = () => {
    const config = quoteConfig || {};
    const headerColor = config.header_color || '#2563eb';
    
    const today = new Date().toLocaleDateString('es-MX', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
    
    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + (formData.valid_days || 30));
    const validUntilStr = validUntil.toLocaleDateString('es-MX', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });

    const priceList = priceLists.find(p => p.id === activePriceListId);
    const labName = config.lab_name || 'Laboratorio Clínico';

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Cotización</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 600px; margin: 0 auto; font-size: 14px; background: white; }
          .header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid ${headerColor}; }
          .header h1 { color: ${headerColor}; font-size: 24px; margin-bottom: 8px; }
          .header .lab-name { font-size: 16px; font-weight: 600; color: #333; }
          .header p { color: #666; font-size: 12px; margin-top: 4px; }
          ${config.logo_header ? `.header img { max-height: 50px; margin-bottom: 12px; }` : ''}
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
          .info-box { background: #f8fafc; padding: 12px; border-radius: 8px; border-left: 3px solid ${headerColor}; }
          .info-box label { font-size: 10px; color: #64748b; text-transform: uppercase; font-weight: 600; }
          .info-box p { font-size: 14px; font-weight: 500; margin-top: 4px; }
          .studies-title { font-size: 12px; color: #64748b; text-transform: uppercase; font-weight: 600; margin-bottom: 12px; }
          .study-item { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e2e8f0; }
          .study-item:last-child { border-bottom: none; }
          .study-name { font-weight: 500; }
          .study-code { font-size: 11px; color: #64748b; font-family: monospace; }
          .study-price { font-weight: 600; color: ${headerColor}; }
          .totals { background: #f8fafc; padding: 16px; border-radius: 8px; margin-top: 20px; }
          .total-row { display: flex; justify-content: space-between; padding: 8px 0; }
          .total-row.final { border-top: 2px solid ${headerColor}; margin-top: 8px; padding-top: 12px; font-size: 18px; font-weight: 700; }
          .total-row.final .amount { color: ${headerColor}; }
          .discount { color: #16a34a; }
          .instructions { margin-top: 20px; padding: 16px; background: #fef3c7; border-radius: 8px; border-left: 3px solid #f59e0b; }
          .instructions h3 { font-size: 12px; color: #92400e; margin-bottom: 8px; }
          .instructions p { color: #78350f; font-size: 13px; }
          .footer { margin-top: 24px; text-align: center; font-size: 11px; color: #94a3b8; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${config.logo_header ? `<img src="${config.logo_header}" alt="Logo" />` : ''}
          <h1>COTIZACIÓN</h1>
          <p class="lab-name">${labName}</p>
          ${config.lab_address ? `<p>${config.lab_address}</p>` : ''}
          ${config.lab_phone ? `<p>Tel: ${config.lab_phone}</p>` : ''}
        </div>
        
        <div class="info-grid">
          <div class="info-box">
            <label>Lista de Precios</label>
            <p>${priceList?.name || 'PARTICULAR'}</p>
          </div>
          <div class="info-box">
            <label>Fecha</label>
            <p>${today}</p>
          </div>
          ${formData.patient_name ? `
          <div class="info-box" style="grid-column: span 2;">
            <label>Paciente</label>
            <p>${formData.patient_name}</p>
          </div>
          ` : ''}
          <div class="info-box" style="grid-column: span 2;">
            <label>Vigencia</label>
            <p>Válida hasta ${validUntilStr}</p>
          </div>
        </div>
        
        <div class="studies-title">Estudios (${selectedStudies.length})</div>
        <div class="studies-list">
          ${selectedStudies.map(s => `
            <div class="study-item">
              <div>
                <div class="study-name">${s.study_name}</div>
                ${s.study_code ? `<div class="study-code">${s.study_code}</div>` : ''}
              </div>
              <div class="study-price">$${s.price.toLocaleString()}</div>
            </div>
          `).join('')}
        </div>
        
        <div class="totals">
          <div class="total-row">
            <span>Subtotal</span>
            <span>$${subtotal.toLocaleString()}</span>
          </div>
          ${discountAmount > 0 ? `
          <div class="total-row discount">
            <span>Descuento</span>
            <span>-$${discountAmount.toLocaleString()}</span>
          </div>
          ` : ''}
          <div class="total-row final">
            <span>TOTAL</span>
            <span class="amount">$${total.toLocaleString()}</span>
          </div>
        </div>
        
        ${formData.instructions ? `
        <div class="instructions">
          <h3>INDICACIONES</h3>
          <p>${formData.instructions}</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>${config.footer_text || `Cotización válida por ${formData.valid_days || 30} días`}</p>
          ${config.terms_text ? `<p style="margin-top: 8px;">${config.terms_text}</p>` : ''}
        </div>
      </body>
      </html>
    `;
  };

  const handleShare = async () => {
    if (selectedStudies.length === 0) {
      toast.error("Agregue al menos un estudio");
      return;
    }

    setGenerating(true);
    try {
      const priceList = priceLists.find(p => p.id === activePriceListId);
      
      // Guardar cotización en el sistema
      await apiCall('/api/quotations', {
        method: 'POST',
        body: JSON.stringify({
          patient_name: formData.patient_name || "Paciente",
          company_name: priceList?.name || "PARTICULAR",
          studies: selectedStudies,
          profiles: [],
          subtotal,
          discount: discountAmount,
          total,
          notes: formData.instructions || null,
          valid_days: formData.valid_days || 30,
          price_list_id: activePriceListId
        })
      });

      // Crear mensaje para WhatsApp
      let message = `*COTIZACIÓN - Laboratorio ComaLab*\n`;
      message += `━━━━━━━━━━━━━━━━\n`;
      if (formData.patient_name) {
        message += `👤 *Paciente:* ${formData.patient_name}\n`;
      }
      message += `📋 *Lista:* ${priceList?.name || 'PARTICULAR'}\n`;
      message += `📅 *Fecha:* ${new Date().toLocaleDateString('es-MX')}\n`;
      message += `━━━━━━━━━━━━━━━━\n\n`;
      
      message += `*ESTUDIOS:*\n`;
      selectedStudies.forEach(s => {
        message += `• ${s.study_name} - $${s.price.toLocaleString()}\n`;
      });
      
      message += `\n━━━━━━━━━━━━━━━━\n`;
      message += `💰 *Subtotal:* $${subtotal.toLocaleString()}\n`;
      if (discountAmount > 0) {
        message += `🏷️ *Descuento:* -$${discountAmount.toLocaleString()}\n`;
      }
      message += `✅ *TOTAL: $${total.toLocaleString()}*\n`;
      message += `━━━━━━━━━━━━━━━━\n`;
      
      if (formData.instructions) {
        message += `\n📝 *Indicaciones:*\n${formData.instructions}\n`;
      }
      
      if (formData.valid_days && formData.valid_days > 0) {
        message += `\n_Vigencia: ${formData.valid_days} días_`;
      }

      // Abrir WhatsApp
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success("Cotización lista para compartir");
      
    } catch (error) {
      toast.error("Error al generar cotización");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (selectedStudies.length === 0) {
      toast.error("Agregue al menos un estudio");
      return;
    }

    const htmlContent = generatePDF();
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cotizacion_${new Date().toISOString().split('T')[0]}.html`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success("Cotización descargada");
  };

  const clearAll = () => {
    setSelectedStudies([]);
    setFormData({ patient_name: "", discount: 0, instructions: "", valid_days: 0 });
    setShowDiscount(false);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5">
              <Calculator className="h-10 w-10 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Cotizador</h1>
            <p className="text-gray-500 text-base mt-2">Inicia sesión para continuar</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label className="text-base text-gray-600">Usuario</Label>
              <Input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Tu usuario"
                className="mt-1 text-lg h-12"
                required
              />
            </div>
            <div>
              <Label className="text-base text-gray-600">Contraseña</Label>
              <Input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Tu contraseña"
                className="mt-1 text-lg h-12"
                required
              />
            </div>
            <Button type="submit" className="w-full h-12 text-base" disabled={loading}>
              <LogIn className="h-5 w-5 mr-2" />
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (loading && studies.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-44 sm:pb-36">
      {/* Header */}
      <div className="bg-blue-600 text-white px-5 py-5 sticky top-0 z-50 shadow-lg">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Calculator className="h-7 w-7 flex-shrink-0" />
            <h1 className="text-xl font-bold truncate">Cotizador</h1>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Select value={activePriceListId} onValueChange={handlePriceListChange}>
              <SelectTrigger className="w-40 bg-blue-700 border-blue-500 text-white text-base h-12">
                <SelectValue placeholder="Lista..." />
              </SelectTrigger>
              <SelectContent>
                {priceLists.map(pl => (
                  <SelectItem key={pl.id} value={pl.id} className="text-base">{pl.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button onClick={handleLogout} className="p-2.5 hover:bg-blue-700 rounded-lg">
              <LogOut className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-5">
        {/* Patient Name */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <Label className="text-base text-gray-500 uppercase font-semibold">Paciente (opcional)</Label>
          <Input
            placeholder="Nombre del paciente..."
            value={formData.patient_name}
            onChange={(e) => setFormData(prev => ({ ...prev, patient_name: e.target.value }))}
            className="mt-2 border-gray-200 text-lg h-14"
          />
        </div>

        {/* Search Studies - Real Time */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-5">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder="Buscar estudio..."
                value={studySearchTerm}
                onChange={(e) => setStudySearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filteredStudies.length > 0) {
                    e.preventDefault();
                    addStudy(filteredStudies[0]);
                  }
                }}
                className="pl-12 pr-12 text-lg h-14"
              />
              {studySearchTerm && (
                <button 
                  onClick={() => setStudySearchTerm("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              )}
            </div>
            
            {/* Results appear as you type */}
            {studySearchTerm && (
              <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5">
                {filteredStudies.slice(0, 15).map((study, index) => {
                  const price = getStudyPrice(study.id);
                  return (
                    <button
                      key={study.id}
                      onClick={() => addStudy(study)}
                      className={`w-full p-4 flex justify-between items-center rounded-lg transition-colors ${
                        index === 0 ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-blue-50'
                      }`}
                    >
                      <div className="text-left min-w-0 flex-1 mr-3">
                        <div className="font-medium text-base truncate">{study.name}</div>
                        {study.code && <div className="text-sm text-gray-500 font-mono">{study.code}</div>}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="font-semibold text-green-600 text-base">${price.toLocaleString()}</span>
                        <Plus className="h-6 w-6 text-blue-600" />
                      </div>
                    </button>
                  );
                })}
                {filteredStudies.length === 0 && (
                  <p className="text-center text-gray-500 py-4 text-lg">No se encontraron estudios</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Selected Studies */}
        {selectedStudies.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 text-lg">
                Estudios ({selectedStudies.length})
              </h3>
              <button onClick={clearAll} className="text-red-500 text-base font-medium">
                Limpiar
              </button>
            </div>
            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
              {selectedStudies.map(study => (
                <div key={study.study_id} className="p-5 flex justify-between items-center">
                  <div className="min-w-0 flex-1 mr-3">
                    <div className="font-medium text-base truncate">{study.study_name}</div>
                    {study.study_code && <div className="text-sm text-gray-500 font-mono">{study.study_code}</div>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-semibold text-green-600 text-base">${study.price?.toLocaleString()}</span>
                    <button onClick={() => removeStudy(study.study_id)} className="p-1.5">
                      <Trash2 className="h-6 w-6 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Discount Toggle */}
        <button 
          onClick={() => setShowDiscount(!showDiscount)}
          className="w-full bg-white rounded-xl p-5 shadow-sm flex items-center justify-between"
        >
          <span className="font-medium text-gray-700 text-base">Descuento, Vigencia e Indicaciones</span>
          <ChevronDown className={`h-6 w-6 text-gray-400 transition-transform ${showDiscount ? 'rotate-180' : ''}`} />
        </button>
        
        {showDiscount && (
          <div className="bg-white rounded-xl p-5 shadow-sm space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-base text-gray-500">Descuento ($)</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={formData.discount || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount: parseFloat(e.target.value) || 0 }))}
                  className="mt-1 text-lg h-12"
                />
              </div>
              <div>
                <Label className="text-base text-gray-500">Vigencia (días)</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="30"
                  value={formData.valid_days === 0 ? "" : formData.valid_days || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, valid_days: e.target.value === "" ? 0 : parseInt(e.target.value) }))}
                  className="mt-1 text-lg h-12"
                />
              </div>
            </div>
            <div>
              <Label className="text-base text-gray-500">Indicaciones</Label>
              <Textarea
                placeholder="Ayuno, preparación especial..."
                value={formData.instructions}
                onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                className="mt-1 text-lg"
                rows={2}
              />
            </div>
          </div>
        )}
      </div>

      {/* Bottom Fixed Bar - Totals & Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="px-5 py-4 space-y-2">
          {/* Totals */}
          <div className="flex justify-between text-base text-gray-600">
            <span>Subtotal ({selectedStudies.length})</span>
            <span>${subtotal.toLocaleString()}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex justify-between text-base text-green-600">
              <span>Descuento</span>
              <span>-${discountAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-xl font-bold">
            <span>Total</span>
            <span className="text-blue-600">${total.toLocaleString()}</span>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              className="flex-1 h-14 text-base"
              onClick={handleDownload}
              disabled={selectedStudies.length === 0}
            >
              <Download className="h-5 w-5 mr-2" />
              PDF
            </Button>
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700 h-14 text-base"
              onClick={handleShare}
              disabled={generating || selectedStudies.length === 0}
            >
              <Share2 className="h-5 w-5 mr-2" />
              {generating ? "..." : "WhatsApp"}
            </Button>
          </div>
        </div>
        {/* Safe area for iPhone notch */}
        <div className="h-safe-area-inset-bottom bg-white"></div>
      </div>
    </div>
  );
}
